import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { Loader2, Upload } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import InterpretationReview from './InterpretationReview';

/**
 * Everything a planner sees to get vendors into the rolodex (PRD §F1, and the
 * 2026-09-01 spreadsheet-import design).
 *
 * One entry point, one file picker, `.csv` or `.xlsx`. She is never asked
 * which importer she wants — she has no basis for that choice and the wrong
 * answer mangles her file quietly. What happens next depends on the file:
 *
 *   - A `.csv` is parsed in the browser (unchanged from the original CSV
 *     importer) straight into `<ColumnMapper>`.
 *   - A `.xlsx` is POSTed to `POST /api/vendors/imports`, which reads it and
 *     replies with a `shape`: `'mapper'` for a clean machine export (routed
 *     to the same `<ColumnMapper>`, fed from the server's column/row
 *     reshaping instead of Papa Parse) or `'interpreter'` for anything else —
 *     a hand-kept rolodex the model has to read structure out of, handed to
 *     `<InterpretationReview>`.
 *
 * Note: the backend's upload route only reads `.xlsx` (`workbook-dump.ts`
 * loads it via `ExcelJS`'s `.xlsx.load`, which cannot parse a raw CSV byte
 * stream). So `.csv` never goes over the wire to that endpoint — the shape
 * split described in the plan/design doc happens for `.xlsx` uploads only.
 * A `.csv` keeps going straight through the browser-side parser it always
 * used, which is also the one place this file's behaviour is unchanged from
 * before this feature existed.
 */

const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

type Stage =
  | { kind: 'choose' }
  | { kind: 'uploading' }
  | { kind: 'mapper'; headers: string[]; rows: Record<string, string>[] }
  | { kind: 'interpreter'; importId: string };

export default function ImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'choose' });

  function reset() {
    setStage({ kind: 'choose' });
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) reset();
  }

  function parseCsv(file: File) {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const cols = result.meta.fields ?? [];
        setStage({ kind: 'mapper', headers: cols, rows: result.data });
      },
      error: () => {
        toast.error('Could not read that CSV');
        setStage({ kind: 'choose' });
      },
    });
  }

  async function uploadSpreadsheet(file: File) {
    setStage({ kind: 'uploading' });
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/api/vendors/imports`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: string; shape?: 'mapper' | 'interpreter'; headers?: string[]; rows?: Record<string, string>[]; id?: string }
        | null;

      if (!res.ok || !body || !body.shape) {
        toast.error(body?.error ?? 'That file could not be imported.');
        setStage({ kind: 'choose' });
        return;
      }

      if (body.shape === 'mapper') {
        setStage({ kind: 'mapper', headers: body.headers ?? [], rows: body.rows ?? [] });
      } else {
        setStage({ kind: 'interpreter', importId: body.id! });
      }
    } catch {
      toast.error('That file could not be uploaded.');
      setStage({ kind: 'choose' });
    }
  }

  function handleFile(file: File) {
    const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
    if (isCsv) {
      parseCsv(file);
    } else {
      void uploadSpreadsheet(file);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import vendors</DialogTitle>
        </DialogHeader>

        {stage.kind === 'choose' && (
          <>
            <div className="space-y-3 py-6 text-center">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
              <Button onClick={() => fileRef.current?.click()}>
                <Upload className="size-4" />
                Choose a file
              </Button>
              <p className="text-xs text-muted-foreground">
                CSV or Excel — any layout works. A hand-kept spreadsheet is read for its
                structure, and you'll confirm what we found before anything is saved.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {stage.kind === 'uploading' && (
          <>
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Reading that file…
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {stage.kind === 'mapper' && (
          <ColumnMapper
            headers={stage.headers}
            rows={stage.rows}
            onCancel={() => handleOpenChange(false)}
            onImported={() => handleOpenChange(false)}
          />
        )}

        {stage.kind === 'interpreter' && (
          <InterpretationReview
            importId={stage.importId}
            onCancel={() => handleOpenChange(false)}
            onImported={() => handleOpenChange(false)}
            onFallback={reset}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Column mapper — extracted unchanged from the original `ImportCsvDialog`.
// Presentational: it takes whatever headers/rows it's handed, whether those
// came from Papa Parse (a `.csv`) or from the server's reshaping of a clean
// `.xlsx` export (`buildMapperPayload` in `vendor-imports.ts`). Neither the
// mapping UI nor the POST to `/api/vendors/import` cares which.
// ---------------------------------------------------------------------------

/** The fields an imported row can map onto. `''` means "skip this column". */
const IMPORT_FIELDS = [
  { key: 'businessName', label: 'Business name (required)' },
  { key: 'contactName', label: 'Contact name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
  { key: 'primaryCategory', label: 'Category' },
  { key: 'notes', label: 'Notes' },
] as const;

/** Column headers we can map without asking. */
function guessField(header: string): string {
  const h = header.toLowerCase().replace(/[^a-z]/g, '');
  if (/(business|company|vendor|studio)?name/.test(h) && !h.includes('contact')) return 'businessName';
  if (h.includes('contact') || h === 'person') return 'contactName';
  if (h.includes('email') || h.includes('mail')) return 'email';
  if (h.includes('phone') || h.includes('tel') || h.includes('mobile')) return 'phone';
  if (h.includes('website') || h.includes('url') || h.includes('site')) return 'website';
  if (h.includes('category') || h.includes('type') || h.includes('service')) return 'primaryCategory';
  if (h.includes('note') || h.includes('comment')) return 'notes';
  return '';
}

function ColumnMapper({
  headers,
  rows,
  onCancel,
  onImported,
}: {
  headers: string[];
  rows: Record<string, string>[];
  onCancel: () => void;
  onImported: () => void;
}) {
  const queryClient = useQueryClient();
  const [mapping, setMapping] = useState<Record<string, string>>(() =>
    Object.fromEntries(headers.map((h) => [h, guessField(h)])),
  );
  const [mergeDuplicates, setMergeDuplicates] = useState(false);

  const mapped = useMemo(() => {
    const nameCol = Object.entries(mapping).find(([, f]) => f === 'businessName')?.[0];
    if (!nameCol) return [];
    return rows
      .map((row) => {
        const out: Record<string, string> = {};
        for (const [col, field] of Object.entries(mapping)) {
          if (field && row[col]?.trim()) out[field] = row[col]!.trim();
        }
        return out;
      })
      .filter((r) => r.businessName);
  }, [rows, mapping]);

  const runImport = useMutation({
    mutationFn: async () => {
      const res = await client.api.vendors.import.$post({
        json: { rows: mapped as never, mergeDuplicates },
      });
      if (!res.ok) throw new Error('Import failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast.success(
        `Imported ${data.created} new${data.merged > 0 ? `, merged ${data.merged}` : ''}${
          data.skipped.length > 0 ? `, skipped ${data.skipped.length} duplicate` : ''
        }`,
      );
      onImported();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {rows.length} rows found. Confirm what each column means.
        </p>

        <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
          {headers.map((h) => (
            <div key={h} className="flex items-center gap-3">
              <span className="w-40 shrink-0 truncate text-sm" title={h}>
                {h}
              </span>
              <span className="text-muted-foreground">→</span>
              <Select
                value={mapping[h] || 'skip'}
                onValueChange={(v) => setMapping({ ...mapping, [h]: v === 'skip' ? '' : v })}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip this column</SelectItem>
                  {IMPORT_FIELDS.map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={mergeDuplicates}
            onChange={(e) => setMergeDuplicates(e.target.checked)}
          />
          Add email addresses to vendors I already have (instead of skipping those rows)
        </label>

        {mapped.length === 0 && (
          <p className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Map one column to "Business name" — nothing can be imported without it.
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={mapped.length === 0 || runImport.isPending} onClick={() => runImport.mutate()}>
          {runImport.isPending ? 'Importing…' : `Import ${mapped.length}`}
        </Button>
      </DialogFooter>
    </>
  );
}
